import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  message,
  Typography,
  Space,
  Tag,
  Row,
  Col,
  Statistic,
  Popconfirm
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DollarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ShopOutlined
} from '@ant-design/icons';
import { dataEntryAPI, stationOwnerAPI } from '../services/api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

interface Transaction {
  _id: string;
  station: {
    _id: string;
    name: string;
  };
  transactionType: 'income' | 'expense';
  category: string;
  description: string;
  amount: number;
  paymentMethod: string;
  invoiceNumber?: string;
  transactionDate: string;
  notes?: string;
  status: string;
}

const Transactions: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [stations, setStations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [filters, setFilters] = useState<any>({
    stationId: 'all',
    transactionType: 'all',
    category: 'all'
  });
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, 'day'),
    dayjs()
  ]);
  const [isMobile, setIsMobile] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchStations();
    fetchTransactions();
  }, [filters, dateRange]);

  const fetchStations = async () => {
    try {
      const response = await stationOwnerAPI.getStations();
      setStations(response);
    } catch (error) {
      message.error('İstasyonlar yüklenemedi');
    }
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filters.stationId !== 'all') params.stationId = filters.stationId;
      if (filters.transactionType !== 'all') params.transactionType = filters.transactionType;
      if (filters.category !== 'all') params.category = filters.category;
      if (dateRange[0]) params.startDate = dateRange[0].format('YYYY-MM-DD');
      if (dateRange[1]) params.endDate = dateRange[1].format('YYYY-MM-DD');

      const response = await dataEntryAPI.getTransactions(params);
      setTransactions(response.transactions || []);
      setSummary(response.summary || null);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlemler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTransaction = () => {
    setEditingTransaction(null);
    form.resetFields();
    form.setFieldsValue({
      transactionDate: dayjs(),
      paymentMethod: 'cash',
      status: 'completed'
    });
    setModalVisible(true);
  };

  const handleEditTransaction = (transaction: Transaction) => {
    setEditingTransaction(transaction);
    form.setFieldsValue({
      station: transaction.station._id,
      transactionType: transaction.transactionType,
      category: transaction.category,
      description: transaction.description,
      amount: transaction.amount,
      paymentMethod: transaction.paymentMethod,
      invoiceNumber: transaction.invoiceNumber,
      transactionDate: dayjs(transaction.transactionDate),
      notes: transaction.notes,
      status: transaction.status
    });
    setModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        stationId: values.station,
        transactionType: values.transactionType,
        category: values.category,
        description: values.description,
        amount: values.amount,
        paymentMethod: values.paymentMethod,
        invoiceNumber: values.invoiceNumber,
        transactionDate: values.transactionDate?.toISOString(),
        notes: values.notes
      };

      if (editingTransaction) {
        await dataEntryAPI.updateTransaction(editingTransaction._id, data);
        message.success('İşlem güncellendi');
      } else {
        await dataEntryAPI.addTransaction(data);
        message.success('İşlem eklendi');
      }

      setModalVisible(false);
      form.resetFields();
      setEditingTransaction(null);
      fetchTransactions();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await dataEntryAPI.deleteTransaction(id);
      message.success('İşlem silindi');
      fetchTransactions();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Silme işlemi başarısız');
    }
  };

  const categoryLabels: any = {
    fuel_sale: 'Yakıt Satışı',
    market_sale: 'Market Satışı',
    car_wash: 'Oto Yıkama',
    restaurant: 'Restoran',
    other_income: 'Diğer Gelir',
    fuel_purchase: 'Yakıt Alımı',
    market_purchase: 'Market Alımı',
    salary: 'Maaş',
    rent: 'Kira',
    utilities: 'Faturalar',
    maintenance: 'Bakım',
    marketing: 'Pazarlama',
    tax: 'Vergi',
    other_expense: 'Diğer Gider'
  };

  const paymentMethodLabels: any = {
    cash: 'Nakit',
    card: 'Kart',
    mobile: 'Mobil',
    bank_transfer: 'Banka Transferi',
    other: 'Diğer'
  };

  const columns = [
    {
      title: 'Tarih',
      dataIndex: 'transactionDate',
      key: 'transactionDate',
      render: (date: string) => dayjs(date).format('DD MMM YYYY'),
      sorter: (a: Transaction, b: Transaction) => 
        dayjs(a.transactionDate).unix() - dayjs(b.transactionDate).unix(),
    },
    {
      title: 'İstasyon',
      dataIndex: ['station', 'name'],
      key: 'station',
    },
    {
      title: 'Tip',
      dataIndex: 'transactionType',
      key: 'transactionType',
      render: (type: string) => (
        <Tag color={type === 'income' ? 'success' : 'error'} icon={type === 'income' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}>
          {type === 'income' ? 'Gelir' : 'Gider'}
        </Tag>
      ),
    },
    {
      title: 'Kategori',
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => categoryLabels[category] || category,
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Tutar',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number, record: Transaction) => (
        <span style={{
          fontWeight: 'bold',
          color: record.transactionType === 'income' ? '#52c41a' : '#ff4d4f',
          fontSize: '16px'
        }}>
          {record.transactionType === 'income' ? '+' : '-'}{amount.toLocaleString('tr-TR')} ₺
        </span>
      ),
      sorter: (a: Transaction, b: Transaction) => a.amount - b.amount,
    },
    {
      title: 'Ödeme Yöntemi',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      render: (method: string) => paymentMethodLabels[method] || method,
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 150,
      render: (_: any, record: Transaction) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditTransaction(record)}
          />
          <Popconfirm
            title="Bu işlemi silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record._id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }} gutter={[16, 16]}>
        <Col xs={24} sm={24} md={12}>
          <Title level={2} style={{ margin: 0, fontSize: '24px' }}>
            <DollarOutlined /> Gelir/Gider Takibi
          </Title>
        </Col>
        <Col xs={24} sm={24} md={12} style={{ textAlign: isMobile ? 'left' : 'right' }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddTransaction} block={isMobile}>
            Yeni İşlem Ekle
          </Button>
        </Col>
      </Row>

      {/* Filtreler */}
      <Card style={{ marginBottom: 24 }} styles={{ body: { padding: '16px' } }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} md={6}>
            <Select
              value={filters.stationId}
              onChange={(value) => setFilters({ ...filters, stationId: value })}
              style={{ width: '100%' }}
              placeholder="İstasyon"
            >
              <Option value="all">Tüm İstasyonlar</Option>
              {stations.map((station: any) => (
                <Option key={station._id} value={station._id}>
                  {station.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              value={filters.transactionType}
              onChange={(value) => setFilters({ ...filters, transactionType: value })}
              style={{ width: '100%' }}
              placeholder="İşlem Tipi"
            >
              <Option value="all">Tümü</Option>
              <Option value="income">Gelir</Option>
              <Option value="expense">Gider</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              value={filters.category}
              onChange={(value) => setFilters({ ...filters, category: value })}
              style={{ width: '100%' }}
              placeholder="Kategori"
            >
              <Option value="all">Tüm Kategoriler</Option>
              <Option value="fuel_sale">Yakıt Satışı</Option>
              <Option value="market_sale">Market Satışı</Option>
              <Option value="car_wash">Oto Yıkama</Option>
              <Option value="fuel_purchase">Yakıt Alımı</Option>
              <Option value="salary">Maaş</Option>
              <Option value="rent">Kira</Option>
              <Option value="utilities">Faturalar</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange([dates[0], dates[1]]);
                }
              }}
              style={{ width: '100%' }}
            />
          </Col>
        </Row>
      </Card>

      {/* Özet İstatistikler */}
      {summary && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Toplam Gelir"
                value={summary.totalIncome}
                prefix={<ArrowUpOutlined />}
                suffix="₺"
                precision={2}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Toplam Gider"
                value={summary.totalExpense}
                prefix={<ArrowDownOutlined />}
                suffix="₺"
                precision={2}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Net Kar"
                value={summary.netProfit}
                prefix={<DollarOutlined />}
                suffix="₺"
                precision={2}
                valueStyle={{
                  color: summary.netProfit >= 0 ? '#52c41a' : '#ff4d4f'
                }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Toplam İşlem"
                value={summary.totalCount}
                prefix={<ShopOutlined />}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Card>
        <Table
          columns={columns}
          dataSource={transactions}
          rowKey="_id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content' }}
          size="small"
        />
      </Card>

      <Modal
        title={editingTransaction ? 'İşlem Düzenle' : 'Yeni İşlem Ekle'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingTransaction(null);
          form.resetFields();
        }}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="station"
            label="İstasyon"
            rules={[{ required: true, message: 'İstasyon seçiniz' }]}
          >
            <Select placeholder="İstasyon seçin">
              {stations.map((station: any) => (
                <Option key={station._id} value={station._id}>
                  {station.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="transactionType"
                label="İşlem Tipi"
                rules={[{ required: true, message: 'İşlem tipi seçiniz' }]}
              >
                <Select placeholder="İşlem tipi">
                  <Option value="income">Gelir</Option>
                  <Option value="expense">Gider</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="category"
                label="Kategori"
                rules={[{ required: true, message: 'Kategori seçiniz' }]}
              >
                <Select placeholder="Kategori">
                  <Option value="fuel_sale">Yakıt Satışı</Option>
                  <Option value="market_sale">Market Satışı</Option>
                  <Option value="car_wash">Oto Yıkama</Option>
                  <Option value="restaurant">Restoran</Option>
                  <Option value="other_income">Diğer Gelir</Option>
                  <Option value="fuel_purchase">Yakıt Alımı</Option>
                  <Option value="market_purchase">Market Alımı</Option>
                  <Option value="salary">Maaş</Option>
                  <Option value="rent">Kira</Option>
                  <Option value="utilities">Faturalar</Option>
                  <Option value="maintenance">Bakım</Option>
                  <Option value="marketing">Pazarlama</Option>
                  <Option value="tax">Vergi</Option>
                  <Option value="other_expense">Diğer Gider</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Açıklama"
            rules={[{ required: true, message: 'Açıklama giriniz' }]}
          >
            <Input placeholder="Açıklama" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="amount"
                label="Tutar (₺)"
                rules={[{ required: true, message: 'Tutar giriniz' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  placeholder="Tutar"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="paymentMethod"
                label="Ödeme Yöntemi"
                rules={[{ required: true, message: 'Ödeme yöntemi seçiniz' }]}
              >
                <Select>
                  <Option value="cash">Nakit</Option>
                  <Option value="card">Kart</Option>
                  <Option value="mobile">Mobil</Option>
                  <Option value="bank_transfer">Banka Transferi</Option>
                  <Option value="other">Diğer</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="transactionDate"
                label="İşlem Tarihi"
                rules={[{ required: true, message: 'Tarih seçiniz' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="invoiceNumber"
                label="Fatura/İrsaliye No"
              >
                <Input placeholder="Fatura no (opsiyonel)" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="notes"
            label="Notlar"
          >
            <TextArea rows={3} placeholder="Notlar (opsiyonel)" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingTransaction ? 'Güncelle' : 'Ekle'}
              </Button>
              <Button onClick={() => {
                setModalVisible(false);
                setEditingTransaction(null);
                form.resetFields();
              }}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Transactions;

